import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import toast, { Toaster } from 'react-hot-toast';
import { FaFingerprint, FaSignInAlt, FaSignOutAlt, FaMapMarkerAlt } from 'react-icons/fa';
import miLogo from './assets/mi-logo.png';

function FichajeMovilView() {
    const [empleados, setEmpleados] = useState([]);
    const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState('');
    const [pin, setPin] = useState('');
    const [cargando, setCargando] = useState(false);

    // Cargamos los trabajadores activos
    useEffect(() => {
        const unsub = onSnapshot(collection(db, 'empleados_asistencia'), (snapshot) => {
            setEmpleados(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsub();
    }, []);

    const registrarFichaje = async (tipo) => {
        if (!empleadoSeleccionado) return toast.error("Selecciona tu nombre primero.");
        if (pin.length !== 4) return toast.error("Ingresa tu PIN de 4 dígitos.");

        const trabajador = empleados.find(emp => emp.id === empleadoSeleccionado);
        if (trabajador.pin !== pin) {
            setPin('');
            return toast.error("PIN incorrecto. Intenta de nuevo.");
        }

        setCargando(true);
        const toastId = toast.loading("Obteniendo ubicación GPS...");

        if (!navigator.geolocation) {
            toast.error("Tu navegador no soporta GPS. Actívalo para fichar.", { id: toastId });
            setCargando(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(async (position) => {
            try {
                toast.loading("Registrando...", { id: toastId });
                await addDoc(collection(db, 'registros_asistencia'), {
                    empleadoId: trabajador.id,
                    nombre: trabajador.nombre,
                    cargo: trabajador.cargo || 'No definido',
                    tipo: tipo, // 'entrada' o 'salida'
                    timestamp: serverTimestamp(),
                    ubicacion: {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                        precision: position.coords.accuracy
                    }
                });
                
                toast.success(`¡${tipo.toUpperCase()} REGISTRADA CON ÉXITO!`, { id: toastId, duration: 4000 });
                setPin('');
                setEmpleadoSeleccionado('');
                setCargando(false);
            } catch (error) {
                console.error(error);
                toast.error("Error al conectar con la base de datos.", { id: toastId });
                setCargando(false);
            }
        }, (error) => {
            console.error(error);
            toast.error("Por favor, permite el acceso a tu ubicación para fichar.", { id: toastId });
            setCargando(false);
        }, { enableHighAccuracy: true, timeout: 10000 });
    };

    return (
        <div className="min-h-screen bg-[#2C3E50] flex flex-col items-center justify-center p-4">
            <Toaster position="top-center" />
            
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all">
                {/* Cabecera */}
                <div className="bg-[#18BC9C] p-6 text-center flex flex-col items-center">
                    <div className="bg-white p-3 rounded-full shadow-lg mb-3">
                        <img src={miLogo} alt="Hoppy Time" className="w-16 h-16 object-contain" />
                    </div>
                    <h1 className="text-2xl font-black text-white uppercase tracking-wider">Control Taller</h1>
                    <p className="text-teal-100 text-xs mt-1 font-semibold flex items-center gap-1">
                        <FaMapMarkerAlt /> GPS ACTIVADO
                    </p>
                </div>

                {/* Formulario */}
                <div className="p-6 space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">¿Quién eres?</label>
                        <select 
                            value={empleadoSeleccionado} 
                            onChange={(e) => setEmpleadoSeleccionado(e.target.value)}
                            className="w-full p-4 border-2 border-gray-200 rounded-xl text-lg text-gray-800 font-semibold focus:border-[#18BC9C] focus:ring-0 outline-none appearance-none bg-gray-50"
                        >
                            <option value="" disabled>Selecciona tu nombre...</option>
                            {empleados.map(emp => (
                                <option key={emp.id} value={emp.id}>{emp.nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                            <FaFingerprint className="text-[#18BC9C]" size={18} /> PIN Secreto
                        </label>
                        <input 
                            type="password" 
                            inputMode="numeric"
                            maxLength="4" 
                            value={pin} 
                            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                            className="w-full p-4 border-2 border-gray-200 rounded-xl text-center text-3xl tracking-[1em] font-black focus:border-[#18BC9C] outline-none bg-gray-50" 
                            placeholder="****" 
                        />
                    </div>

                    {/* Botones de Fichaje */}
                    <div className="grid grid-cols-2 gap-4 pt-4">
                        <button 
                            disabled={cargando}
                            onClick={() => registrarFichaje('entrada')}
                            className="flex flex-col items-center justify-center p-4 bg-gray-800 hover:bg-black text-white rounded-xl transition-transform active:scale-95 disabled:opacity-50"
                        >
                            <FaSignInAlt size={28} className="mb-2 text-[#18BC9C]" />
                            <span className="font-bold tracking-wide">ENTRADA</span>
                        </button>
                        
                        <button 
                            disabled={cargando}
                            onClick={() => registrarFichaje('salida')}
                            className="flex flex-col items-center justify-center p-4 bg-gray-100 hover:bg-gray-200 text-gray-800 border-2 border-gray-200 rounded-xl transition-transform active:scale-95 disabled:opacity-50"
                        >
                            <FaSignOutAlt size={28} className="mb-2 text-red-500" />
                            <span className="font-bold tracking-wide">SALIDA</span>
                        </button>
                    </div>
                </div>
                
                <div className="bg-gray-50 p-3 text-center text-xs text-gray-400 border-t border-gray-100">
                    Hoppy Time App © {new Date().getFullYear()}
                </div>
            </div>
        </div>
    );
}

export default FichajeMovilView;
